import { format } from "fecha";
import { platformModifierKey } from "ol/events/condition";
import { boundingExtent } from "ol/extent";
import type { Extent } from "ol/extent";
import type { FeatureLike } from "ol/Feature";
import Feature from "ol/Feature";
import PointGeom from "ol/geom/Point";
import DragBoxInteraction from "ol/interaction/DragBox";
import VectorLayer from "ol/layer/Vector";
import OlMap from "ol/Map";
import Overlay from "ol/Overlay";
import { fromLonLat, transformExtent } from "ol/proj";
import VectorSource from "ol/source/Vector";
import View from "ol/View";

import type { Epicenter } from "../../../api/epicenter/structs";
import { calcEpicenterStyle } from "../../../common/calcstyle";
import { DragMessageControl } from "../../../common/dragmessagecontrol";
import { EpicenterLegendControl } from "../../../common/epicenerlegendcontrol";
import { constants } from "../../../constants";
import { pageUrls } from "../../../urls";
import { orderBy } from "../../../utils/arrayutil/orderby";
import hx from "../../../utils/hydroxie";
import { InheritableEventEmitter } from "../../../utils/inheritableeventemitter";
import { gsiBlank, gsiPale, gsiPhotoAndPale, gsiStd, gsiStdEnglish } from "../../../utils/maputil/createbaselayer";
import { createControls } from "../../../utils/maputil/createcontrols";
import { createInteractions } from "../../../utils/maputil/createinteractions";
import { findNearestFeature } from "../../../utils/maputil/findnearestfeature";
import LayerSwitcher from "../../../utils/maputil/ollayerswitchermod";

/**
 * 震源検索→地図エリア
 */
export class MapArea
    extends InheritableEventEmitter<{ selectExtent: [extent: Extent] }> {

    /** 地図エリアのルート要素 */
    #elem: HTMLElement;
    /** 震源データのベクターソース */
    #source: VectorSource<Feature<PointGeom>>;

    /**
     * コンストラクター。
     *
     * @param elem 地図エリアのルート要素
     */
    constructor(elem: HTMLElement) {
        super();

        this.#elem = elem;
        this.#source = new VectorSource({});
    }

    /**
     * 初期化処理を行う。
     * OpenLayers地図を構築し、クリックイベントやドラッグボックスインタラクションを設定する。
     */
    async init() {
        const controls = createControls();
        controls.push(new LayerSwitcher());
        controls.push(new EpicenterLegendControl());
        controls.push(new DragMessageControl());

        const interactions = createInteractions();
        const drag = new DragBoxInteraction({ condition: platformModifierKey });
        drag.on("boxend", () => {
            const extent3857 = drag.getGeometry().getExtent();
            const extent4326 = transformExtent(extent3857, "EPSG:3857", "EPSG:4326");
            this.emit("selectExtent", extent4326);
        });
        interactions.push(drag);

        const sourcesLayer = new VectorLayer({
            source: this.#source,
            style: (feature: FeatureLike) => {
                const magnitude = feature.get("magnitude") as number;
                return calcEpicenterStyle(magnitude);
            },
        });

        const overlayContentElem = hx("div", { className: "ol-feature-overlay" });
        const overlay = new Overlay({
            autoPan: true,
            positioning: "top-center",
            offset: [0, 16],
            element: overlayContentElem,
        });

        const map = new OlMap({
            target: this.#elem,
            layers: [
                gsiStdEnglish({ visible: false }),
                gsiPhotoAndPale({ visible: false }),
                gsiBlank({ visible: false }),
                gsiPale({ visible: true }),
                gsiStd({ visible: false }),
                sourcesLayer,
            ],
            controls,
            overlays: [overlay],
            interactions,
            view: new View({
                center: fromLonLat(constants.search.defaultMapCenter),
                zoom: constants.search.defaultZoom,

                extent: boundingExtent([fromLonLat([180, 80]), fromLonLat([-180, -80])]),
                enableRotation: false,
                constrainResolution: true,
                smoothExtentConstraint: true,
                minZoom: 2,
                maxZoom: 18,
                smoothResolutionConstraint: true,
                multiWorld: false,
            }),
        });

        map.on("click", (e) => {
            const features = map.getFeaturesAtPixel(e.pixel, { layerFilter: (l) => l === sourcesLayer }) as Feature<PointGeom>[];
            if (!features.length) {
                overlay.setPosition(undefined);
                return;
            }

            const nearest = findNearestFeature(e.coordinate, features);
            const eqSourceId = nearest.get("eqSourceId") as Epicenter["eqSourceId"];
            const originTime = nearest.get("originTime") as Epicenter["jemOriginTime"];
            const magnitude = nearest.get("magnitude") as Epicenter["mjma"];
            const depth = nearest.get("depth") as Epicenter["jemDepth"];
            const url = pageUrls["/event/[eqSourceId]"].get({ params: { eqSourceId } });

            const content = [
                hx("div",
                    "地震発生時刻: ",
                    format(new Date(originTime), "YYYY-MM-DD HH:mm:ss")),
                hx("div",
                    "Mj: ",
                    magnitude),
                hx("div",
                    "深さ: ",
                    `${depth} km`),
                hx("div",
                    hx("a", { href: url, target: "_blank" }, "詳細")),
            ];

            overlayContentElem.replaceChildren(...content);
            overlay.setPosition(nearest.getGeometry()?.getCoordinates());
        });
    }

    /**
     * 震源データを地図上に表示する。
     *
     * @param epicenters 表示する震源データの配列
     */
    setEpicenters(epicenters: Epicenter[] | undefined) {
        const source = this.#source;
        if (epicenters?.length) {
            const features = orderBy(epicenters)
                .map((x) => {
                    const coord4326 = [x.jemLon ?? x.nfLon, x.jemLat ?? x.nfLat];
                    const coord3857 = fromLonLat(coord4326);

                    const feature = new Feature(new PointGeom(coord3857));

                    feature.set("eqSourceId", x.eqSourceId);
                    feature.set("originTime", x.jemOriginTime);
                    feature.set("magnitude", x.mjmaStr ?? x.mwStr);
                    feature.set("depth", x.jemDepthStr);

                    return feature;
                });

            source.clear();
            source.addFeatures(features);
        } else {
            source.clear();
        }
    }
}
